import axios from "axios";
import { Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  capitalize,
  formatDate,
  formatDateTime,
  getFormattedBookingDateTime,
} from "../../../utils/helpers";
import StatusBadge from "../../common/StatusBadge";

const ViewCustomerDetailsModal = ({ customer, onClose }) => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const userId = customer?._id;

  useEffect(() => {
    if (!userId) return;

    const fetchBookings = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/service-bookings/get-all/by-user-id/${userId}`
        );

        setBookings(res?.data?.data ?? []);
      } catch (error) {
        console.error("Failed to fetch customer bookings", error);
        setBookings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [userId]);

  if (!customer) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="bg-white w-full max-w-5xl rounded-xl h-[85vh] overflow-y-auto shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <h2 className="text-lg font-semibold">Customer Details</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        <section className="mb-6">
          <h3 className="font-semibold text-gray-700 mb-2">
            Profile Information
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <p>
              <b>Name:</b> {customer.name}
            </p>
            <p>
              <b>Phone:</b> {customer.phoneNumber}
            </p>
            <p>
              <b>Email:</b> {customer.email}
            </p>
            <p>
              <b>Gender:</b> {capitalize(customer.gender)}
            </p>
            <p className="sm:col-span-2">
              <b>Address:</b> {customer.address || "—"}
            </p>
            <p>
              <b>Customer Since:</b> {formatDate(customer.createdAt)}
            </p>
            <p>
              <b>Total Bookings:</b> {customer.totalBookings ?? bookings.length}
            </p>
            {customer.lastBookingDate && (
              <p>
                <b>Last Booking:</b> {formatDateTime(customer.lastBookingDate)}
              </p>
            )}
          </div>
        </section>

        <section>
          <h3 className="font-semibold text-gray-700 mb-3">Booking History</h3>

          {isLoading ? (
            <CustomerBookingsSkeleton />
          ) : bookings.length === 0 ? (
            <EmptyBookingsState />
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => {
                const { date, slotTime } = getFormattedBookingDateTime({
                  slotAvailabilityId: booking?.slotAvailabilityId,
                  slotSnapshot: booking?.slotSnapshot,
                });
                return (
                  <div
                    key={booking._id}
                    className="border rounded-lg p-4 space-y-3"
                  >
                    {/* Top Row */}
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium">
                        Booking ID: {booking._id}
                      </p>
                      <StatusBadge status={booking.status} />
                    </div>

                    {/* Booking Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <p>
                        <b>Booking Date:</b> <span>{date}</span>
                      </p>
                      <p>
                        <b>Booking Slot:</b> <span>{slotTime}</span>
                      </p>
                      <p>
                        <b>Total Amount:</b> ₹{booking.totalAmount}
                      </p>

                      {booking.rescheduledDate && (
                        <p>
                          <b>Rescheduled:</b>{" "}
                          {formatDateTime(booking.rescheduledDate)}
                        </p>
                      )}
                    </div>

                    {/* Services */}
                    <div className="border rounded divide-y">
                      {booking.services.map((s) => (
                        <div
                          key={s._id}
                          className="p-3 flex justify-between gap-4 text-sm"
                        >
                          <div>
                            <p className="font-medium">{s.serviceId?.name}</p>
                            <p className="text-xs text-gray-500">
                              Brand: {s.brand} • {s.serviceId?.duration}
                            </p>
                          </div>
                          <div className="font-semibold">₹{s.price}</div>
                        </div>
                      ))}
                    </div>

                    {/* Cancel Reason */}
                    {booking.status === "cancelled" && booking.cancelReason && (
                      <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
                        <b>Cancel Reason:</b> {booking.cancelReason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ViewCustomerDetailsModal;

const CustomerBookingsSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="border rounded-lg p-4 space-y-3">
        <div className="h-4 w-40 bg-gray-200 rounded" />
        <div className="h-4 w-64 bg-gray-200 rounded" />
        <div className="h-20 bg-gray-200 rounded" />
      </div>
    ))}
  </div>
);

const EmptyBookingsState = () => (
  <div className="py-12 flex flex-col items-center gap-3 text-gray-500">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
      <Users className="w-7 h-7 text-gray-400" />
    </div>
    <p className="text-sm font-medium">No bookings found for this customer</p>
  </div>
);
// import axios from "axios";
// import { Users, X } from "lucide-react";
// import { useEffect, useState } from "react";
// import {
//   capitalize,
//   formatDate,
//   formatDateTime
// } from "../../../utils/helpers";
// import StatusBadge from "../../common/StatusBadge";

// const ViewCustomerDetailsModal = ({ customer, onClose }) => {
//   const [bookings, setBookings] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);

//   const userId = customer?._id;

//   useEffect(() => {
//     if (!userId) return;

//     const fetchBookings = async () => {
//       try {
//         const res = await axios.get(
//           `${process.env.REACT_APP_API_URL}/service-bookings/get-all/by-user-id/${userId}`
//         );

//         setBookings(res?.data?.data ?? []);
//       } catch (error) {
//         console.error("Failed to fetch customer bookings", error);
//         setBookings([]);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchBookings();
//   }, [userId]);

//   if (!customer) return null;

//   return (
//     <div
//       className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
//       onClick={onClose}
//     >
//       {/* Modal */}
//       <div
//         className="bg-white w-full max-w-5xl rounded-xl h-[85vh] overflow-y-auto shadow-xl p-6"
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* Header */}
//         <div className="flex items-center justify-between border-b pb-3 mb-4">
//           <h2 className="text-lg font-semibold">Customer Details</h2>
//           <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
//             <X size={18} />
//           </button>
//         </div>

//         <section className="mb-6">
//           <h3 className="font-semibold text-gray-700 mb-2">
//             Profile Information
//           </h3>

//           <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
//             <p>
//               <b>Name:</b> {customer.name}
//             </p>
//             <p>
//               <b>Phone:</b> {customer.phoneNumber}
//             </p>
//             <p>
//               <b>Email:</b> {customer.email}
//             </p>
//             <p>
//               <b>Gender:</b> {capitalize(customer.gender)}
//             </p>
//             <p className="sm:col-span-2">
//               <b>Address:</b> {customer.address || "—"}
//             </p>
//             <p>
//               <b>Customer Since:</b> {formatDate(customer.createdAt)}
//             </p>
//             <p>
//               <b>Total Bookings:</b> {customer.totalBookings ?? bookings.length}
//             </p>
//             {customer.lastBookingDate && (
//               <p>
//                 <b>Last Booking:</b> {formatDateTime(customer.lastBookingDate)}
//               </p>
//             )}
//           </div>
//         </section>

//         <section>
//           <h3 className="font-semibold text-gray-700 mb-3">Booking History</h3>

//           {isLoading ? (
//             <CustomerBookingsSkeleton />
//           ) : bookings.length === 0 ? (
//             <EmptyBookingsState />
//           ) : (
//             <div className="space-y-4">
//               {bookings.map((booking) => {
//                 return (
//                   <div
//                     key={booking._id}
//                     className="border rounded-lg p-4 space-y-3"
//                   >
//                     {/* Top Row */}
//                     <div className="flex justify-between items-center">
//                       <p className="text-sm font-medium">
//                         Booking ID: {booking._id}
//                       </p>
//                       <StatusBadge status={booking.status} />
//                     </div>

//                     {/* Booking Info */}
//                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
//                       <p>
//                         <b>Booking Date:</b>{" "}
//                         {formatDateTime(booking.bookingDate)}
//                       </p>
//                       <p>
//                         <b>Total Amount:</b> ₹{booking.totalAmount}
//                       </p>

//                       {booking.rescheduledDate && (
//                         <p>
//                           <b>Rescheduled:</b>{" "}
//                           {formatDateTime(booking.rescheduledDate)}
//                         </p>
//                       )}
//                     </div>

//                     {/* Services */}
//                     <div className="border rounded divide-y">
//                       {booking.services.map((s) => (
//                         <div
//                           key={s._id}
//                           className="p-3 flex justify-between gap-4 text-sm"
//                         >
//                           <div>
//                             <p className="font-medium">{s.serviceId?.name}</p>
//                             <p className="text-xs text-gray-500">
//                               Brand: {s.brand} • {s.serviceId?.duration}
//                             </p>
//                           </div>
//                           <div className="font-semibold">₹{s.price}</div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Cancel Reason */}
//                     {booking.status === "cancelled" && booking.cancelReason && (
//                       <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
//                         <b>Cancel Reason:</b> {booking.cancelReason}
//                       </div>
//                     )}
//                   </div>
//                 );
//               })}
//             </div>
//           )}
//         </section>
//       </div>
//     </div>
//   );
// };

// export default ViewCustomerDetailsModal;

// const CustomerBookingsSkeleton = () => (
//   <div className="space-y-4 animate-pulse">
//     {Array.from({ length: 3 }).map((_, i) => (
//       <div key={i} className="border rounded-lg p-4 space-y-3">
//         <div className="h-4 w-40 bg-gray-200 rounded" />
//         <div className="h-4 w-64 bg-gray-200 rounded" />
//         <div className="h-20 bg-gray-200 rounded" />
//       </div>
//     ))}
//   </div>
// );

// const EmptyBookingsState = () => (
//   <div className="py-12 flex flex-col items-center gap-3 text-gray-500">
//     <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
//       <Users className="w-7 h-7 text-gray-400" />
//     </div>
//     <p className="text-sm font-medium">No bookings found for this customer</p>
//   </div>
// );
